import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { publicSubscribeVacancies } from "@/lib/api/vacancy-sources.functions";
import { toast } from "sonner";
import { BellRing } from "lucide-react";

/**
 * Public opt-in form for vacancy email alerts. On submit sends a
 * double-opt-in confirmation email; only confirmed subscribers get
 * notifications. Filters (sector/location/keyword) are optional.
 */
export function VacancyAlertsSubscribe() {
  const fn = useServerFn(publicSubscribeVacancies);
  const [email, setEmail] = useState("");
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res: any = await fn({
        data: {
          email,
          keyword: keyword || null,
          location_contains: location || null,
        },
      });
      if (res.ok) {
        setDone(true);
        toast.success(
          res.alreadyConfirmed
            ? "You're already subscribed."
            : "Check your inbox to confirm your subscription.",
        );
      } else {
        toast.error("Could not subscribe — please try again.");
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Subscribe failed");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <section className="rounded-lg border border-navy/20 bg-navy/5 p-5">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-navy" />
          <h2 className="font-display text-lg text-navy">Almost done</h2>
        </div>
        <p className="mt-2 text-sm text-foreground/80">
          We've sent a confirmation email to <strong>{email}</strong>. Click the link
          inside to activate your alerts. You can unsubscribe at any time.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2">
        <BellRing className="h-4 w-4 text-navy" />
        <h2 className="font-display text-lg text-navy">Get vacancy alerts by email</h2>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        We monitor company career pages and email you when matching roles are posted.
        You always apply directly on the employer's own site — we never ask for fees.
      </p>
      <form onSubmit={submit} className="mt-4 grid gap-3 md:grid-cols-4">
        <label className="text-xs md:col-span-2">
          Email
          <input
            type="email"
            required
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="text-xs">
          Keyword (optional)
          <input
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
            placeholder="e.g. candidate attorney"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </label>
        <label className="text-xs">
          Location contains
          <input
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
            placeholder="e.g. Gauteng"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </label>
        <div className="md:col-span-4">
          <button
            type="submit"
            disabled={busy}
            className="rounded bg-navy px-4 py-2 text-sm font-medium text-navy-foreground disabled:opacity-60"
          >
            {busy ? "Subscribing…" : "Subscribe to alerts"}
          </button>
        </div>
      </form>
    </section>
  );
}
